/*
===============================================================================================================
                                 Copyright 2022-2023, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, { useEffect, forwardRef } from 'react';
import PropTypes from 'prop-types';
import ErrorReportingHOC from '@/components/error-reporting-hoc';
import TextDiv from '@/components/text-div';
/**
 * RadioButtonWrapper for radio buttons error reporting
 * Is wrapped in the ErrorReporting HOC.
 * @param {JSX.Element} children - Standard React children. Content to be wrapped
 * @param {string} inputSelectorId - DOM id, used to identify this element
 * @param {string} selectedItemId - The item id of the radio button that is selected.
 * If none is passed in, nothing is selected. Pass in the currently selected
 * radio button to be wrapped.
 */
const RadioButtonWrapper = forwardRef(({
  children,
  inputSelectorId,
  selectedItemId,
  setContent,
  error,
}, ref) => {
  useEffect(() => {
    setContent(selectedItemId);
  }, [selectedItemId]);

  return (
    <>
      <div id={inputSelectorId} ref={ref}>
        {children}
      </div>
      <div className={`error${error ? ' visible' : ''}`}>
        {error && <TextDiv>{error}</TextDiv>}
      </div>
      <style jsx>
        {`
          .error {
            visibility: hidden;
            margin-top: 3px;
            width: 100%;
            white-space: break-spaces;
          }

          .error.visible {
            visibility: visible;
            color: var(--color-error-red);
            font-size: var(--font-size-error);
            margin-top: 14px;
            margin-bottom: 20px;
            text-align: left;
          }

        `}
      </style>
    </>
  );
});

RadioButtonWrapper.defaultProps = {
  inputSelectorId: null,
  children: null,
  selectedItemId: null,
  error: null,
};

RadioButtonWrapper.propTypes = {
  inputSelectorId: PropTypes.string,
  children: PropTypes.node,
  setContent: PropTypes.func.isRequired,
  selectedItemId: PropTypes.string,
  error: PropTypes.string,
};

export default ErrorReportingHOC(RadioButtonWrapper);
